"use client";

import { usePathname } from "next/navigation";
import React, { JSX, useEffect, useRef } from "react";
import IconClose from "@/app/(components)/generics/Icons/IconClose";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./Modal.module.css";

export enum ModalType {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export default function Modal(): JSX.Element {
  const T = useT();
  const {
    isOpen,
    title,
    component,
    footer,
    type,
    closeWhenClickOutside,
    closeModal,
    overlays,
    closeOverlay,
  } = ModalStore();

  const overlay = overlays.length > 0 ? overlays[overlays.length - 1] : null;

  const pathname = usePathname();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    if (previousPathname.current === pathname) {
      return;
    }

    previousPathname.current = pathname;
    closeModal();
  }, [pathname, closeModal]);

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!closeWhenClickOutside) {
      return;
    }

    if (event.target === event.currentTarget) {
      closeModal();
    }
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      closeOverlay();
    }
  };

  const typeClassName = (modalType: ModalType | null): string => {
    switch (modalType) {
      case ModalType.MEDIUM:
        return styles.medium;
      case ModalType.LARGE:
        return styles.large;
      default:
        return styles.small;
    }
  };

  const handleContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const paginationBtn = target.closest("button");
    const hasPaginationParent = target.closest('[class*="Pagination_"]');

    if (paginationBtn && hasPaginationParent) {
      event.currentTarget.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Pagination lives in the footer for some modals; scroll the content (its previous
  // sibling) back to the top when a page is changed there.
  const handleFooterClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("button") && target.closest('[class*="Pagination_"]')) {
      const content = event.currentTarget.previousElementSibling as HTMLElement | null;
      content?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <div
        className={UStyles.classNames(styles.background, !isOpen ? styles.close : "")}
        onPointerDown={handleModalClick}
      >
        <div className={UStyles.classNames(styles.modal, typeClassName(type))} id="modal">
          <div className={styles.header}>
            <div className={styles.title}>{title ? T(title as TKey) : title}</div>
            <button className={styles.closeButton} onClick={closeModal}>
              <IconClose />
            </button>
          </div>

          <div className={styles.content} onClick={handleContentClick}>
            {component}
          </div>

          {footer && (
            <div className={styles.footer} onClick={handleFooterClick}>
              {footer}
            </div>
          )}
        </div>
      </div>

      {overlay && (
        <div
          key={overlays.length}
          className={UStyles.classNames(styles.background, styles.overlay)}
          onPointerDown={handleOverlayClick}
        >
          <div className={UStyles.classNames(styles.modal, typeClassName(overlay.type))}>
            <div className={styles.header}>
              <div className={styles.title}>
                {overlay.title ? T(overlay.title as TKey) : overlay.title}
              </div>
              <button className={styles.closeButton} onClick={closeOverlay}>
                <IconClose />
              </button>
            </div>

            <div className={styles.content} onClick={handleContentClick}>
              {overlay.component}
            </div>

            {overlay.footer && (
              <div className={styles.footer} onClick={handleFooterClick}>
                {overlay.footer}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
